import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ScrollFadeIn from "./Scrollfadein";
import { projectsData } from "./Data";
import { useIsMobile } from "./Useismobile";

const INITIAL_VISIBLE = 6;

export default function Projects({ theme }) {
  const { fg, muted, border, accent, bg, darkMode } = theme;
  const isMobile = useIsMobile();

  const [showAll, setShowAll]     = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);

  const visible = showAll ? projectsData : projectsData.slice(0, INITIAL_VISIBLE);
  const hidden  = projectsData.length - INITIAL_VISIBLE;
  const preview = projectsData[activeIdx];

  return (
    <section style={{ padding: isMobile ? "40px 20px" : "60px 40px", maxWidth: 1200, margin: "0 auto" }}>

      {/* ── Header ── */}
      <ScrollFadeIn>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "baseline",
          marginBottom: 32, paddingBottom: 16, borderBottom: `1px solid ${border}`,
        }}>
          <span style={{ fontSize: 10, letterSpacing: "0.2em", color: muted }}>SELECTED WORK</span>
          <span style={{ fontSize: 10, letterSpacing: "0.2em", color: muted }}>
            {String(projectsData.length).padStart(2, "0")} PROJECTS
          </span>
        </div>
      </ScrollFadeIn>

      {/* ── Mobile: stacked cards ── */}
      {isMobile ? (
        <div>
          <AnimatePresence>
            {visible.map(({ title, description, link, techStack, screenshot }, i) => (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.4, delay: i < INITIAL_VISIBLE ? i * 0.05 : 0 }}
                style={{
                  marginBottom: 20,
                  border: `1px solid ${border}`,
                  borderRadius: 16,
                  overflow: "hidden",
                }}
              >
                {/* Screenshot */}
                <div style={{ width: "100%", aspectRatio: "16/9", overflow: "hidden", background: darkMode ? "#111" : "#eee" }}>
                  <img
                    src={screenshot}
                    alt={title}
                    style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }}
                    draggable={false}
                  />
                </div>

                {/* Info */}
                <div style={{ padding: "16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <span style={{
                      fontFamily: "'Bebas Neue', sans-serif",
                      fontSize: 22, fontWeight: 400, letterSpacing: "0.02em", color: fg,
                    }}>
                      {title.toUpperCase()}
                    </span>
                    <a
                      href={link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: 9, letterSpacing: "0.12em",
                        padding: "5px 12px",
                        border: `1px solid ${accent}`,
                        borderRadius: 100, color: accent,
                        textDecoration: "none", whiteSpace: "nowrap", flexShrink: 0,
                      }}
                    >
                      VISIT ↗
                    </a>
                  </div>
                  <p style={{ fontSize: 12, color: muted, lineHeight: 1.7, marginBottom: 10 }}>
                    {description}
                  </p>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {techStack.map(t => (
                      <span key={t} style={{
                        fontSize: 9, letterSpacing: "0.1em",
                        padding: "2px 9px", border: `1px solid ${border}`,
                        borderRadius: 100, color: muted,
                      }}>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* View more */}
          <motion.button
            onClick={() => setShowAll(!showAll)}
            whileTap={{ scale: 0.97 }}
            style={{
              width: "100%", padding: "14px 0",
              fontSize: 10, letterSpacing: "0.18em",
              border: `1px solid ${border}`, borderRadius: 12,
              background: "transparent", color: fg,
              cursor: "pointer", fontFamily: "inherit",
              marginTop: 8,
            }}
          >
            {showAll ? "SHOW LESS ↑" : `VIEW ALL PROJECTS  (+${hidden} MORE) ↓`}
          </motion.button>
        </div>

      ) : (
        /* ── Desktop: split list + sticky preview ── */
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48, alignItems: "start" }}>

          {/* LEFT: rows */}
          <div>
            <AnimatePresence>
              {visible.map(({ title, description, link, techStack }, i) => {
                const isActive = activeIdx === i;
                return (
                  <motion.div
                    key={title}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.4, delay: i < INITIAL_VISIBLE ? i * 0.05 : 0 }}
                    onMouseEnter={() => setActiveIdx(i)}
                    style={{ padding: "22px 0", borderBottom: `1px solid ${border}`, cursor: "pointer" }}
                  >
                    <div style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
                      <span style={{
                        fontSize: 9, color: isActive ? accent : muted,
                        letterSpacing: "0.1em", minWidth: 24,
                        transition: "color 0.2s", fontVariantNumeric: "tabular-nums",
                      }}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{
                            fontFamily: "'Bebas Neue', sans-serif",
                            fontSize: "clamp(22px, 2.8vw, 38px)", fontWeight: 400,
                            letterSpacing: "0.02em", color: isActive ? fg : muted,
                            transition: "color 0.2s", lineHeight: 1,
                          }}>
                            {title.toUpperCase()}
                          </span>
                          <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{
                              fontSize: 9, letterSpacing: "0.12em", padding: "5px 12px",
                              border: `1px solid ${isActive ? accent : border}`,
                              borderRadius: 100, color: isActive ? accent : muted,
                              textDecoration: "none", transition: "all 0.2s",
                              whiteSpace: "nowrap", flexShrink: 0, marginLeft: 12,
                            }}
                          >
                            VISIT ↗
                          </a>
                        </div>

                        <AnimatePresence>
                          {isActive && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.25 }}
                              style={{ overflow: "hidden" }}
                            >
                              <p style={{ fontSize: 11, color: muted, lineHeight: 1.7, marginTop: 8, marginBottom: 10 }}>
                                {description}
                              </p>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                                {techStack.map(t => (
                                  <span key={t} style={{
                                    fontSize: 9, letterSpacing: "0.1em",
                                    padding: "2px 9px", border: `1px solid ${accent}`,
                                    borderRadius: 100, color: accent,
                                  }}>
                                    {t}
                                  </span>
                                ))}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>

            {/* View more */}
            <div style={{ marginTop: 36 }}>
              <motion.button
                onClick={() => setShowAll(!showAll)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                style={{
                  width: "100%", padding: "14px 0",
                  fontSize: 10, letterSpacing: "0.18em",
                  border: `1px solid ${border}`, borderRadius: 12,
                  background: "transparent", color: fg,
                  cursor: "pointer", fontFamily: "inherit", transition: "all 0.25s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = fg; e.currentTarget.style.color = bg; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = fg; }}
              >
                {showAll ? "SHOW LESS ↑" : `VIEW ALL PROJECTS  (+${hidden} MORE) ↓`}
              </motion.button>
            </div>
          </div>

          {/* RIGHT: sticky preview */}
          <div style={{ position: "sticky", top: 100, alignSelf: "start" }}>
            <motion.div
              layout
              style={{
                borderRadius: 16, overflow: "hidden",
                border: `1px solid ${border}`,
                background: darkMode ? "#111" : "#f0f0ec",
                boxShadow: darkMode ? "0 32px 80px rgba(0,0,0,0.5)" : "0 32px 80px rgba(0,0,0,0.08)",
              }}
            >
              {/* Browser bar */}
              <div style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "12px 16px", borderBottom: `1px solid ${border}`,
                background: darkMode ? "#181818" : "#e8e8e4",
              }}>
                {["#ff5f57", "#febc2e", "#28c840"].map((c, i) => (
                  <div key={i} style={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                ))}
                <div style={{
                  flex: 1, margin: "0 10px", padding: "4px 12px",
                  borderRadius: 6, background: darkMode ? "#222" : "#fff",
                  fontSize: 9, letterSpacing: "0.05em", color: muted,
                  overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
                }}>
                  {preview.link.replace("https://", "")}
                </div>
                <span style={{ fontSize: 9, color: muted }}>↗</span>
              </div>

              {/* Screenshot */}
              <div style={{ position: "relative", width: "100%", aspectRatio: "16/10", overflow: "hidden" }}>
                <AnimatePresence mode="wait">
                  <motion.img
                    key={activeIdx}
                    src={preview.screenshot}
                    alt={preview.title}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.97 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top", display: "block" }}
                    draggable={false}
                  />
                </AnimatePresence>

                {/* Overlay */}
                <div style={{
                  position: "absolute", bottom: 0, left: 0, right: 0,
                  padding: "32px 20px 16px",
                  background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                    <div>
                      <p style={{ fontSize: 9, letterSpacing: "0.15em", color: "rgba(255,255,255,0.6)", marginBottom: 4 }}>
                        PROJECT {String(activeIdx + 1).padStart(2, "0")} / {String(projectsData.length).padStart(2, "0")}
                      </p>
                      <p style={{ fontSize: 15, fontFamily: "'Bebas Neue', sans-serif", color: "#fff", letterSpacing: "0.05em" }}>
                        {preview.title.toUpperCase()}
                      </p>
                    </div>
                    <a
                      href={preview.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: 9, letterSpacing: "0.13em", padding: "7px 14px",
                        border: "1px solid rgba(255,255,255,0.4)", borderRadius: 100,
                        color: "#fff", textDecoration: "none",
                        background: "rgba(255,255,255,0.1)", backdropFilter: "blur(6px)",
                        transition: "all 0.2s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = accent; e.currentTarget.style.borderColor = accent; e.currentTarget.style.color = "#000"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.1)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.4)"; e.currentTarget.style.color = "#fff"; }}
                    >
                      OPEN SITE ↗
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Dots */}
            <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 16 }}>
              {visible.map((_, i) => (
                <div
                  key={i}
                  onClick={() => setActiveIdx(i)}
                  style={{
                    width: activeIdx === i ? 20 : 6, height: 6,
                    borderRadius: 100,
                    background: activeIdx === i ? accent : border,
                    cursor: "pointer", transition: "all 0.3s ease",
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}